import { useRef, useState } from "react";

interface OtpCodeInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  invalid?: boolean;
}

export function OtpCodeInput({ id, label, value, onChange, autoFocus, invalid }: OtpCodeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  function placeCaretAtEnd() {
    const input = inputRef.current;
    if (!input) return;
    input.setSelectionRange(input.value.length, input.value.length);
  }

  return (
    <label className="flex flex-col gap-2 w-full" htmlFor={id}>
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <span className="relative flex items-center justify-between gap-2 sm:gap-2.5">
        <input
          ref={inputRef}
          id={id}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={value}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 6))}
          onPaste={(event) => {
            event.preventDefault();
            onChange(event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6));
          }}
          onFocus={() => { setFocused(true); placeCaretAtEnd(); }}
          onBlur={() => setFocused(false)}
          onClick={placeCaretAtEnd}
          aria-invalid={invalid || undefined}
          autoFocus={autoFocus}
        />
        {Array.from({ length: 6 }, (_, index) => {
          const isActive = focused && index === Math.min(value.length, 5);
          const isFilled = !!value[index];
          return (
            <span
              key={index}
              className={`flex-1 h-13 sm:h-14 flex items-center justify-center text-xl font-bold rounded-xl transition-all select-none border-2 ${
                invalid
                  ? "border-rose-400 bg-rose-50/40 text-rose-700"
                  : isActive
                  ? "border-[#012699] ring-4 ring-[#012699]/15 bg-white text-slate-900 shadow-sm"
                  : isFilled
                  ? "border-slate-300 bg-slate-50/70 text-slate-900"
                  : "border-slate-200 bg-white text-slate-400"
              }`}
              aria-hidden="true"
            >
              {value[index] || ""}
            </span>
          );
        })}
      </span>
    </label>
  );
}
